export type ScoutState = "idle" | "wave" | "point" | "celebrate";

export type TourId = "welcome" | "settings" | "quick-start";

type BaseTourStep = {
    title: string;
    content: React.ReactNode;
    scoutState: ScoutState;
    route?: string;
    /** When true, advancing past this step ends the tour (marks as dismissed, not completed). */
    endsTour?: boolean;
};

type CenterStep = BaseTourStep & {
    placement: "center";
};

type TargetedStep = BaseTourStep & {
    placement: "top" | "bottom" | "left" | "right";
    target: string;
    disableBeacon?: boolean;
    spotlightClicks?: boolean;
    waitForTarget?: boolean;
    /** Override the default 5s waitForTarget timeout (in ms). */
    waitForTargetTimeout?: number;
    actionLabel?: string;
    disableAutoScroll?: boolean;
    /** When waitForTarget times out, enter this named group instead of falling back to center. */
    onTimeoutGroup?: string;
    /** If this CSS selector is present (or appears during waitForTarget), immediately trigger onTimeoutGroup instead of continuing to wait for the target. */
    bailoutSelector?: string;
    /** When this CSS selector appears in the DOM, automatically advance to the next step. Used for steps that should transition based on UI state changes (e.g., "processing audio" → "generating document"). */
    advanceWhenSelector?: string;
    /** CSS selector that must match an element in the DOM for the Next button to be enabled. Polled reactively while the step is visible. */
    enableNextSelector?: string;
};

export type TourStep = CenterStep | TargetedStep;

export function isTargetedStep(step: TourStep): step is TargetedStep {
    return step.placement !== "center";
}

/** Get the CSS selector target for a step. Center steps use 'body'. */
export function stepTarget(step: TourStep): string {
    return isTargetedStep(step) ? step.target : "body";
}

export type TourTrack = "paid" | "trial";

export type TourConfig = {
    id: TourId;
    name: string;
    description: string;
    track?: TourTrack;
    showInHelpMenu: boolean;
    steps: [TourStep, ...TourStep[]];
    /** Named step groups for branching (entered via onTimeoutGroup on targeted steps). */
    groups?: Record<string, [TourStep, ...TourStep[]]>;
};

export type PersistedTourState =
    | { status: "completed"; lastStepIndex: number; completedAt: string; dismissedAt: null }
    | { status: "dismissed"; lastStepIndex: number; completedAt: null; dismissedAt: string };

export type TourStates = Partial<Record<TourId, PersistedTourState>>;

/** Runtime validation for data deserialized from localStorage. */
export function isPersistedTourState(value: unknown): value is PersistedTourState {
    if (typeof value !== "object" || value === null) return false;
    const v = value as Record<string, unknown>;
    if (typeof v.lastStepIndex !== "number" || !Number.isInteger(v.lastStepIndex) || v.lastStepIndex < 0) return false;

    // Each status requires its own timestamp and the other must be null
    if (v.status === "completed") {
        return typeof v.completedAt === "string" && v.dismissedAt === null;
    }
    if (v.status === "dismissed") {
        return typeof v.dismissedAt === "string" && v.completedAt === null;
    }
    return false;
}
